import { ApiResponse } from 'services/api/interfaces/ApiResponse'
import { ExternalOption, LoadExternalList } from '../ShelterBuddyExternalField'

/** Minimal shape every Shelter Buddy list row shares. */
export type SbRow = { Name: string; Id: number }

/**
 * Throws a descriptive error when a Shelter Buddy response isn't a 200, matching
 * the message format the per-form loaders used before this was shared.
 */
export const ensureSbOk = (response: ApiResponse<unknown>, noun: string): void => {
  if (response.data.status !== 200) {
    const message =
      response.data?.message ||
      `Failed to load ${noun} (status ${response.data?.status ?? 'unknown'})`
    console.error(response.data)
    throw new Error(message)
  }
}

/**
 * Map raw Shelter Buddy rows to the `{ label, name, value }` option shape used by
 * the external pickers. By default `value` is the row Id (stringified); pass a
 * custom selector for endpoints that key off another field (e.g. size → Name).
 */
export const mapSbOptions = <T extends SbRow>(
  rows: T[] | null | undefined,
  value: (row: T) => string = row => row.Id.toString(),
): ExternalOption[] =>
  rows?.map(row => ({
    label: `${row.Name} [${row.Id}]`,
    name: row.Name,
    value: value(row),
  })) ?? []

/**
 * Build a loader for a simple Shelter Buddy list endpoint that takes only an
 * integration uuid and returns the rows directly in `response.data.data`.
 *
 * Endpoints with extra params or a nested payload (locations → `data.Data`,
 * states → `country_id`, animal types → `filters`) keep their own loader and
 * call `ensureSbOk` + `mapSbOptions` directly.
 */
export const makeSbLoader = <T extends SbRow>(
  fn: (params: { integration_uuid: string }) => Promise<ApiResponse<T[]>>,
  noun: string,
  value?: (row: T) => string,
): LoadExternalList =>
  async (integrationUuid: string) => {
    const response = await fn({ integration_uuid: integrationUuid })
    ensureSbOk(response, noun)
    return mapSbOptions(response.data.data, value)
  }
